import { Record } from 'immutable';
import type { RecordOf } from 'immutable';

import type { ValueOf } from 'type-fest';
import type { QcsSyncResourceStore } from '../core/resourceStoreTypes';
import type { transactionsTypes } from '../transactions';
import type { RecurrenceType } from './customFrequencyTypes';
import { FrequencyEnum } from './frequency';

export const SCHEDULED_TRANSACTIONS_REDUCER_KEY = 'scheduledTransactionsStore';

export const mkRecurrence = Record<RecurrenceType>({
  frequency: FrequencyEnum.MONTHLY,
  interval: 1,
  count: null,
  byMonthDay: null,
  byDay: null,
  bySetPos: null,
  byMonth: null,
  byYearDay: null,
  byDates: null,
  endOn: null,
  alias: null,
});

export const STTypeEnum = Object.freeze({
  EXPENSE: 'EXPENSE',
  INCOME: 'INCOME',
  TRANSFER: 'TRANSFER',
  BILL: 'BILL',
  SUBSCRIPTION: 'SUBSCRIPTION',
  UNKNOWN: 'UNKNOWN',
});

export const STExcludeEnum = Object.freeze({
  SRC: 'SRC',
  DEST: 'DEST',
  BOTH: 'BOTH',
  NONE: 'NONE',
});
export type STType = ValueOf<typeof STTypeEnum>;
export type STExclude = ValueOf<typeof STExcludeEnum>;

export type AutoAdjustAmount =
  | 'AVERAGE_PAID'
  | 'EBILL_BALANCE_DUE'
  | 'EBILL_MINIMUM_DUE'
  | 'EBILL_STATEMENT_BALANCE'
  | 'TIME_OF_YEAR'
  | 'NONE'
  | null;

export type ScheduledTransactionType = {
  id?: string;
  clientId?: string;
  isDeleted?: boolean;
  createdAt?: string;
  modifiedAt?: string | null;
  userModifiedAt?: string | null;
  dueOn?: string;
  type?: string;
  recurrence?: RecurrenceType;
  transaction?: transactionsTypes.TransactionProps | transactionsTypes.TransactionRecord | null;
  overrideNextDueOn?: string | null;
  overrideNextAmount?: number | null;
  reminderDays?: number | null;
  autoAcceptDays?: number | null;
  autoSendDays?: number | null;
  autoAdjustAmount?: AutoAdjustAmount;
  autoAdjustDueOn?: boolean | null;
  averagePaidCount?: number | null;
  paymentProvider?: string | null;
  paymentModelId?: string | null;
  providerBillerId?: string | null; // use this ID to lookup the biller
  billPresentmentAccountId?: string | null; // use this ID to lookup the billerAccount
  billPresentmentBillerId?: string | null; // This is used in place of a proper login ID until QCS adds it
  billPresentmentProvider?: string | null;
  billPresentmentModelId?: string | null;
  relatedWebsite?: string | null;
  printCheck?: boolean | null;
  isCompleted?: boolean | null;
  isUserVerified?: boolean | null;
  excludeF2S?: string | null;
  excludeReports?: string | null;
  resetMatchAmountVariance?: boolean | null;
  matchAnyAmount?: boolean | null;
  matchAmountVariance?: number | null;
};

export type ScheduledTransaction = RecordOf<ScheduledTransactionType>;
export const mkScheduledTransaction = Record<ScheduledTransactionType>({
  id: undefined,
  clientId: undefined,
  isDeleted: undefined,
  createdAt: undefined,
  modifiedAt: undefined,
  userModifiedAt: undefined,
  dueOn: undefined,
  type: undefined,
  recurrence: undefined,
  transaction: undefined,
  overrideNextDueOn: undefined,
  overrideNextAmount: undefined,
  reminderDays: undefined,
  autoAcceptDays: undefined,
  autoSendDays: undefined,
  autoAdjustAmount: undefined,
  autoAdjustDueOn: undefined,
  averagePaidCount: undefined,
  paymentProvider: undefined,
  paymentModelId: undefined,
  providerBillerId: undefined,
  billPresentmentAccountId: undefined,
  billPresentmentBillerId: undefined,
  billPresentmentProvider: undefined,
  billPresentmentModelId: undefined,
  relatedWebsite: undefined,
  printCheck: undefined,
  isCompleted: false, // that's important for undo
  isUserVerified: undefined,
  excludeF2S: undefined,
  excludeReports: undefined,
  resetMatchAmountVariance: undefined,
  matchAnyAmount: undefined,
  matchAmountVariance: undefined,
});

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [SCHEDULED_TRANSACTIONS_REDUCER_KEY]: QcsSyncResourceStore<ScheduledTransactionType>;
  }
}
